import { formatCoding, isResource, resolveId } from '@medplum/core'
import type {
  Coding,
  Encounter,
  EncounterDiagnosis,
  Group,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { findKey } from 'lodash-es'
import { z } from 'zod'
import { ENCOUNTER_CLASS_SYSTEM } from '#src/codeSystems.ts'
import { ExtendedResource, type ExportFields } from '#src/ExtendedResource.ts'
import { isOfType, now, recordEntries, recordKeys } from '#src/utils.ts'

export type ExtendedEncounterArgs = Omit<
  Encounter,
  'resourceType' | 'status' | 'subject' | 'class' | 'participant' | 'period'
> & {
  status: Encounter['status']
  class: keyof typeof ExtendedEncounter.ENCOUNTER_CLASS
  patient: Reference<Patient>
  practitioner?: Reference<Practitioner>
  startTime?: string
  endTime?: string
}

export class ExtendedEncounter extends ExtendedResource<Encounter> {
  static readonly RESOURCE_TYPE = 'Encounter'
  static readonly BASE_QUERY_STRUCTURE = {
    _sort: 'date',
    _count: 1000,
  } as const

  constructor({
    status,
    class: encounterClass,
    patient,
    practitioner,
    startTime,
    endTime,
    ...rest
  }: ExtendedEncounterArgs) {
    super({
      ...rest,
      status,
      class: ExtendedEncounter.ENCOUNTER_CLASS[encounterClass],
      subject: patient,
      participant: practitioner ? [{ individual: practitioner }] : undefined,
    })
    if (startTime) {
      this.startTime = startTime
    }
    if (endTime) {
      this.endTime = endTime
    }
  }

  static readonly ENCOUNTER_CLASS = {
    ONSITE: {
      system: ENCOUNTER_CLASS_SYSTEM,
      code: 'onsite',
      display: 'Onsite',
    },
    APPOINTMENT: {
      system: ENCOUNTER_CLASS_SYSTEM,
      code: 'appointment',
      display: 'Appointment',
    },
    HOSPITAL_INPATIENT: {
      system: ENCOUNTER_CLASS_SYSTEM,
      code: 'hospital-inpatient',
      display: 'Hospital Inpatient',
    },
    EMERGENCY: {
      system: ENCOUNTER_CLASS_SYSTEM,
      code: 'emergency',
      display: 'Emergency',
    },
    HOME_HEALTH: {
      system: ENCOUNTER_CLASS_SYSTEM,
      code: 'home-health',
      display: 'Home Health',
    },
    NON_VISIT: {
      system: ENCOUNTER_CLASS_SYSTEM,
      code: 'non-visit',
      display: 'Non-Visit',
    },
  } satisfies Record<string, Coding>

  static readonly STATUSES = {
    PLANNED: 'planned',
    ARRIVED: 'arrived',
    TRIAGED: 'triaged',
    IN_PROGRESS: 'in-progress',
    FINISHED: 'finished',
    CANCELLED: 'cancelled',
  } satisfies Record<string, Encounter['status']>

  static readonly CLASS_OPTIONS = recordEntries(this.ENCOUNTER_CLASS).map(
    ([key, value]) => ({
      value: key,
      label: formatCoding(value) ?? '',
    })
  )

  static readonly classSchema = z.enum(
    recordKeys(this.ENCOUNTER_CLASS) as [keyof typeof this.ENCOUNTER_CLASS]
  )

  get subject(): Reference<Patient | Group> | undefined {
    return this.resource.subject
  }

  set subject(value: Reference<Patient>) {
    this.resource.subject = value
  }

  get diagnosis(): EncounterDiagnosis[] {
    return this.resource.diagnosis ?? []
  }

  set diagnosis(value: EncounterDiagnosis[]) {
    this.resource.diagnosis = value.length > 0 ? value : undefined
  }

  get practitioner(): Reference<Practitioner> | undefined {
    return this.resource.participant?.find(
      (p) => p.individual && isOfType(p.individual, 'Practitioner')
    )?.individual as Reference<Practitioner> | undefined
  }

  get startTime(): string | undefined {
    return this.resource.period?.start
  }

  set startTime(value: string | undefined) {
    this.resource.period ??= {}
    this.resource.period.start = value
  }

  get endTime(): string | undefined {
    return this.resource.period?.end
  }

  set endTime(value: string | undefined) {
    this.resource.period ??= {}
    this.resource.period.end = value
  }

  start(dateTime: string = now()): void {
    this.status = 'in-progress'
    this.startTime = dateTime
  }

  finish(dateTime: string = now()): void {
    this.status = 'finished'
    this.endTime = dateTime
  }

  get status(): Encounter['status'] {
    return this.resource.status
  }
  set status(value: Encounter['status']) {
    this.resource.status = value
  }

  get class(): Coding {
    return this.resource.class
  }

  get classKey(): keyof typeof ExtendedEncounter.ENCOUNTER_CLASS | undefined {
    return findKey(
      ExtendedEncounter.ENCOUNTER_CLASS,
      (value) =>
        value.code === this.class.code && value.system === this.class.system
    ) as keyof typeof ExtendedEncounter.ENCOUNTER_CLASS | undefined
  }

  set class(value: Coding) {
    this.resource.class = value
  }

  static isInstance(value: unknown): value is Encounter {
    return isResource(value) && isOfType(value, 'Encounter')
  }

  protected getExportFields(): ExportFields {
    return {
      id: this.id,
      status: this.status,
      class: this.resource.class,
      patientId: resolveId(this.subject),
      practitionerId: resolveId(this.practitioner),
      startTime: this.startTime,
      endTime: this.endTime,
      diagnosisConditionIds: this.diagnosis
        .filter((d) => d.condition && isOfType(d.condition, 'Condition'))
        .map((d) => resolveId(d.condition))
        .filter((id): id is string => Boolean(id)),
    }
  }
}
